import { Users } from "lucide-react";
import React, { useCallback } from "react";
import { Badge, Button, useMantineTheme } from "@mantine/core";
import { ISegmentationBlock } from "../../@types/segmentationBlocks";
import { formatDateTimeISO } from "../../../../utils/DateTime";

interface SegmentationBlockCardProps {
  block: ISegmentationBlock;
  type: "active" | "inactive";
  onEdit?: (block: ISegmentationBlock) => void;
}

export const SegmentationBlockCard: React.FC<SegmentationBlockCardProps> = ({
  block,
  type,
  onEdit,
}) => {
  const theme = useMantineTheme();
  const handleEdit = useCallback(() => {
    onEdit?.(block);
  }, [block, onEdit]);

  // Display title from config, or first segment name as fallback
  const displayName =
    block.config?.title || block.segments[0]?.name || "Untitled Block";

  // Get created by info
  const createdByName = block.created_by
    ? `${block.created_by.first_name} ${block.created_by.last_name}`
    : "Unknown";

  // Use theme colors instead of hardcoded values
  const indicatorColor =
    type === "active"
      ? theme.colors.pink[4]
      : theme.fn.rgba(theme.colors.dark[9], 0.4);

  return (
    <div className="flex items-center justify-between py-4 [&:not(:last-child)]:border-b border-[#cdd3d6]">
      <div className="flex gap-4 items-center">
        <div
          className="w-8 h-8 rounded-full shrink-0"
          style={{
            backgroundColor: indicatorColor,
          }}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="font-bold text-[16px] text-[#062133]">
              {displayName}
            </span>
            <Badge
              variant="light"
              color="gray"
              size="md"
              leftSection={<Users size={14} />}
              styles={{
                root: {
                  backgroundColor: "rgba(6, 33, 51, 0.08)",
                  color: "#062133",
                  fontWeight: 500,
                  fontSize: "11px",
                  textTransform: "none",
                  paddingLeft: "8px",
                  paddingRight: "12px",
                },
              }}
            >
              {block.total_matched_employee_count ?? 0} employees
            </Badge>
          </div>
          <div className="text-[12px] text-[#839099] leading-4">
            Created: {formatDateTimeISO(block.created_at)} by {createdByName}
          </div>
        </div>
      </div>
      <Button
        variant="outline"
        size="xs"
        onClick={handleEdit}
        styles={{
          root: {
            borderColor: "#062133",
            color: "#062133",
            fontWeight: 600,
            fontSize: "12px",
            padding: "8px 12px",
            borderRadius: "6px",
          },
        }}
      >
        Edit
      </Button>
    </div>
  );
};

export default SegmentationBlockCard;
